import axios from "axios";
import { PAYMENT_ENDPOINTS } from "./payment.constants";

/**
 * Opens HDFC payment in a popup window and monitors payment completion
 * @param {Object} params - Payment parameters
 * @param {string} params.paymentUrl - HDFC payment URL
 * @param {string} params.orderId - HDFC order ID
 * @param {string} params.chargerId - Charger ID
 * @param {string} params.connectorId - Connector ID
 * @param {number} params.companyId - Company ID
 * @param {Function} submitHandler - Callback function to handle payment result
 */
export async function displayHdfcPayment({
  paymentUrl,
  orderId,
  chargerId,
  connectorId,
  companyId,
  submitHandler,
}) {
  // Open payment page in a popup window
  const width = 600;
  const height = 700;
  const left = window.screen.width / 2 - width / 2;
  const top = window.screen.height / 2 - height / 2;

  const popup = window.open(
    paymentUrl,
    "HDFC Payment",
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );

  // Check if popup was blocked - more reliable detection
  let popupBlocked = false;
  
  if (!popup) {
    popupBlocked = true;
  } else {
    // Try to check if popup is closed (might throw cross-origin error)
    try {
      if (popup.closed) {
        popupBlocked = true;
      }
    } catch (e) {
      // Cross-origin error - popup is on different domain, which means it opened successfully
      // This is good - popup is not blocked
      popupBlocked = false;
    }
  }

  if (popupBlocked) {
    // Popup was blocked - provide fallback with better UX
    const instructions = 
      "Popup was blocked by your browser.\n\n" +
      "Options:\n" +
      "1. Click OK to open payment in a new tab\n" +
      "2. Click Cancel and allow popups, then try again\n\n" +
      "To allow popups:\n" +
      "• Look for a popup icon in your browser's address bar\n" +
      "• Click it and select 'Always allow popups from this site'\n" +
      "• Then refresh and try again";

    const userChoice = window.confirm(instructions);

    if (userChoice) {
      // Open in new tab as fallback
      try {
        const newTab = window.open(paymentUrl, "_blank", "noopener,noreferrer");
        if (newTab) {
          // Start polling for payment status after user has time to complete payment
          setTimeout(() => {
            verifyHdfcPayment(orderId, companyId, submitHandler);
          }, 10000); // Give user 10 seconds to start payment
          
          submitHandler({
            isDone: false,
            status: false,
            message: "Payment page opened in new tab. Please complete the payment there. Payment status will be checked automatically.",
          });
        } else {
          // Even new tab was blocked
          submitHandler({
            isDone: true,
            status: false,
            message: "Unable to open payment page. Please check your browser settings to allow popups/new tabs for this site.",
          });
        }
      } catch (error) {
        submitHandler({
          isDone: true,
          status: false,
          message: "Unable to open payment page. Please allow popups/new tabs in your browser settings.",
        });
      }
    } else {
      submitHandler({
        isDone: true,
        status: false,
        message: "Please allow popups for this site in your browser settings and try again.",
      });
    }
    return;
  }

  // Verify popup is still open after a brief moment (catches cases where it closes immediately)
  // Only check if we didn't already detect blocking
  if (!popupBlocked) {
    setTimeout(() => {
      try {
        if (popup && popup.closed) {
          // Popup closed immediately - likely was blocked
          const userChoice = window.confirm(
            "The payment window closed unexpectedly. Would you like to open the payment page in a new tab instead?"
          );
          if (userChoice) {
            const newTab = window.open(paymentUrl, "_blank", "noopener,noreferrer");
            if (newTab) {
              setTimeout(() => {
                verifyHdfcPayment(orderId, companyId, submitHandler);
              }, 10000);
              submitHandler({
                isDone: false,
                status: false,
                message: "Payment page opened in new tab. Please complete the payment there. Payment status will be checked automatically.",
              });
            }
          }
          return;
        }
      } catch (e) {
        // Cross-origin error is expected when popup is on HDFC domain - this is good
        // Continue with normal monitoring
      }
    }, 500);
  }

  // Monitor popup for payment completion
  let isChecking = false;
  let checkInterval = null;

  const checkPaymentStatus = async () => {
    if (isChecking) return;
    
    try {
      // Check if popup is closed (user might have completed payment)
      if (popup.closed) {
        if (checkInterval) clearInterval(checkInterval);
        isChecking = true;
        // Poll the backend to check payment status
        await verifyHdfcPayment(orderId, companyId, submitHandler);
        return;
      }

      // Check popup URL for success/failure indicators
      try {
        const popupUrl = popup.location.href;

        // If the popup is on our receipt page (/payment/hdfc-receipt), let it
        // manage its own closing after the countdown. Stop the recursive
        // checkPaymentStatus polling here — but DO NOT clear checkInterval, so
        // it can detect popup.closed and call verifyHdfcPayment afterwards.
        if (popupUrl.includes("/payment/hdfc-receipt")) {
          return;
        }

        // Use path-specific checks — avoid matching query-param values like
        // "?status=paid" that appear on our own receipt page URL.
        const popupPath = (() => {
          try { return new URL(popupUrl).pathname; } catch { return popupUrl; }
        })();

        // If popup reached a known external success path
        if (
          popupPath.includes("/payment-success") ||
          popupPath.includes("/success")
        ) {
          if (checkInterval) clearInterval(checkInterval);
          isChecking = true;
          popup.close();
          await verifyHdfcPayment(orderId, companyId, submitHandler);
          return;
        }

        // If popup reached a known external failure/cancel path
        if (
          popupPath.includes("/payment-failed") ||
          popupPath.includes("/failure") ||
          popupPath.includes("/cancel")
        ) {
          if (checkInterval) clearInterval(checkInterval);
          isChecking = true;
          popup.close();
          submitHandler({
            isDone: true,
            status: false,
            message: "Payment was cancelled or failed. Please try again.",
          });
          return;
        }
      } catch (e) {
        // Cross-origin error - popup is on HDFC's domain (expected).
        // Continue monitoring popup closure.
      }

      // Continue checking if popup is still open
      if (!popup.closed && !isChecking) {
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      // Continue checking if popup is still open
      if (!popup.closed && !isChecking) {
        setTimeout(checkPaymentStatus, 2000);
      }
    }
  };

  // Start monitoring after a short delay
  setTimeout(checkPaymentStatus, 2000);

  // Also listen for popup close event with interval
  checkInterval = setInterval(() => {
    if (popup.closed && !isChecking) {
      clearInterval(checkInterval);
      isChecking = true;
      verifyHdfcPayment(orderId, companyId, submitHandler);
    }
  }, 1000);
}

/**
 * Verify HDFC payment status with backend
 * @param {string} orderId - HDFC order ID
 * @param {number} companyId - Company ID
 * @param {Function} submitHandler - Callback function
 */
async function verifyHdfcPayment(orderId, companyId, submitHandler) {
  try {
    // Poll payment status - adjust endpoint based on your backend API
    const maxAttempts = 15; // Poll for up to 45 seconds (15 * 3s)
    let attempts = 0;

    const pollStatus = async () => {
      attempts++;
      
      try {
        // Try to verify payment status
        const response = await axios.post(
          PAYMENT_ENDPOINTS.getHdfcVerifyPaymentUrl,
          {
            orderId,
            companyId,
          }
        );

        if (response?.data?.success || response?.data?.status === "paid" || response?.data?.status === "SUCCESS") {
          submitHandler({
            isDone: true,
            status: true,
            message:
              "Payment is successful! Please wait, charger will start in a while.",
          });
          return;
        }

        if (response?.data?.status === "failed" || response?.data?.status === "FAILED" || response?.data?.status === "cancelled") {
          submitHandler({
            isDone: true,
            status: false,
            message: "Payment was cancelled or failed. Please try again.",
          });
          return;
        }

        // If still processing and haven't exceeded max attempts, poll again
        if (attempts < maxAttempts && (response?.data?.status === "processing" || response?.data?.status === "CREATED" || response?.data?.status === "PENDING")) {
          setTimeout(pollStatus, 3000);
        } else if (attempts >= maxAttempts) {
          // Timeout - assume payment is still processing (webhook will handle it)
          submitHandler({
            isDone: true,
            status: true,
            message:
              "Payment is being processed. Please wait, charger will start in a while.",
          });
        }
      } catch (error) {
        console.error("Error verifying HDFC payment:", error);
        
        // If endpoint doesn't exist (404) or other error, handle gracefully
        if (error.response?.status === 404) {
          // Endpoint doesn't exist - backend webhook should handle verification
          // Show success message and let backend handle the rest
          submitHandler({
            isDone: true,
            status: true,
            message:
              "Payment is being processed. Please wait, charger will start in a while.",
          });
        } else if (attempts >= maxAttempts) {
          // Max attempts reached - assume processing (webhook will handle)
          submitHandler({
            isDone: true,
            status: true,
            message:
              "Payment is being processed. Please wait, charger will start in a while.",
          });
        } else {
          // Retry on other errors
          setTimeout(pollStatus, 3000);
        }
      }
    };

    // Start polling after a short delay to allow webhook to process
    setTimeout(pollStatus, 2000);
  } catch (error) {
    console.error("Error in verifyHdfcPayment:", error);
    // Fallback: assume payment is processing (webhook will handle verification)
    submitHandler({
      isDone: true,
      status: true,
      message:
        "Payment is being processed. Please wait, charger will start in a while.",
    });
  }
}

